import { z } from "zod";
import { effectiveInternalRateBasisSchema } from "./rule-drafts.js";
/**
 * Import of a rule proposal plan: `POST /v1/rule-proposals:import-plan`.
 *
 * A plan is a reviewed, read-only file (for example the output of
 * `plan-effective-burden-proposals.ts`) that lists the rule changes an owner
 * wants to put through the ordinary review. Importing it never approves or
 * publishes anything: a dry run reports what would be created, and a commit
 * creates PENDING_REVIEW proposals through the same write path as
 * `POST /v1/rule-proposals`. Every item is idempotent by plan hash + item.
 *
 * Plans are tagged by `kind` so new plan kinds can be added without changing
 * the endpoint: add its schema to `proposalPlanSchema` and a handler on the API.
 */
export const PROPOSAL_PLAN_SCHEMA_VERSION = "1.0.0";
/** Items one plan may carry; one import is one transaction. */
export const PROPOSAL_PLAN_MAX_ITEMS = 500;
export const PROPOSAL_PLAN_MAX_ADDITIONAL_EVIDENCE = 20;
export const EFFECTIVE_BURDEN_PLAN_KIND = "effective-burden-proposal-plan";
export const PROPOSAL_PLAN_KINDS = [EFFECTIVE_BURDEN_PLAN_KIND];
export const proposalPlanKindSchema = z.enum(PROPOSAL_PLAN_KINDS);
const uuidSchema = z.string().uuid();
const ncmSchema = z.string().regex(/^\d{8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const rateSchema = z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
const ufSchema = z.string().regex(/^[A-Z]{2}$/);
const planHashSchema = z.string().regex(/^[a-f0-9]{64}$/);
const codeSchema = z.string().regex(/^[A-Z][A-Z0-9_]{1,59}$/);
const proposedRuleSchema = z.record(z.string(), z.unknown());
/* -- effective-burden-proposal-plan ------------------------------------------ */
const effectiveBurdenItemSchema = z
    .object({
    operation: z.literal("SUPERSEDE"),
    supersedesRuleId: uuidSchema,
    /** The same rule (id, window, evidence) with the burden and its basis added. */
    proposedRule: proposedRuleSchema,
    route: z.string().min(1).max(20),
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    interstateRate: rateSchema.nullable(),
})
    .strict()
    .superRefine((item, context) => {
    const rule = item.proposedRule;
    const issue = (path, message) => context.addIssue({ code: "custom", path: ["proposedRule", ...path], message });
    if (typeof rule.id !== "string")
        issue(["id"], "Is required");
    const taxes = rule.taxes;
    const icms = taxes?.icms;
    if (!icms || typeof icms !== "object")
        return issue(["taxes", "icms"], "Is required");
    if (typeof icms.effectiveInternalRate !== "string") {
        issue(["taxes", "icms", "effectiveInternalRate"], "Is required");
    }
    else if (!rateSchema.safeParse(icms.effectiveInternalRate).success) {
        issue(["taxes", "icms", "effectiveInternalRate"], "Must be a decimal fraction");
    }
    if (!effectiveInternalRateBasisSchema.safeParse(icms.effectiveInternalRateBasis).success) {
        issue(["taxes", "icms", "effectiveInternalRateBasis"], "Must state the legal basis");
    }
    if (!Array.isArray(rule.evidence))
        return issue(["evidence"], "Must be an array");
    rule.evidence.forEach((reference, index) => {
        const evidenceId = reference?.evidenceId;
        if (!uuidSchema.safeParse(evidenceId).success) {
            issue(["evidence", index, "evidenceId"], "Must identify an evidence document");
        }
    });
});
export const effectiveBurdenPlanSchema = z
    .object({
    schemaVersion: z.literal(PROPOSAL_PLAN_SCHEMA_VERSION),
    kind: z.literal(EFFECTIVE_BURDEN_PLAN_KIND),
    /** Where the plan was generated from; informational, not part of the plan hash. */
    generatedFrom: z.string().max(500).optional(),
    note: z.string().max(1_000).optional(),
    criteria: z
        .object({
        destinationUf: ufSchema,
        cestSegments: z
            .array(z.string().regex(/^\d{2}$/))
            .min(1)
            .max(20),
        effectiveInternalRate: rateSchema,
        basis: effectiveInternalRateBasisSchema,
    })
        .strict(),
    items: z.array(effectiveBurdenItemSchema).max(PROPOSAL_PLAN_MAX_ITEMS),
    /** Why each other rule was left alone; informational, not part of the plan hash. */
    skipped: z
        .array(z.object({ ruleId: z.string().min(1).max(100), reason: codeSchema }).strict())
        .max(50_000),
    summary: z
        .object({
        considered: z.number().int().nonnegative(),
        planned: z.number().int().nonnegative(),
        skipped: z.record(z.string(), z.number().int().nonnegative()),
    })
        .strict(),
})
    .strict()
    .superRefine((plan, context) => {
    const seen = new Set();
    plan.items.forEach((item, index) => {
        if (seen.has(item.supersedesRuleId)) {
            context.addIssue({
                code: "custom",
                path: ["items", index, "supersedesRuleId"],
                message: "Each rule may be superseded once per plan",
            });
        }
        seen.add(item.supersedesRuleId);
    });
});
/** Every plan kind the endpoint understands. */
export const proposalPlanSchema = z.discriminatedUnion("kind", [effectiveBurdenPlanSchema]);
/* -- request ----------------------------------------------------------------- */
export const proposalPlanImportModeSchema = z.enum(["DRY_RUN", "COMMIT"]);
/**
 * `POST /v1/rule-proposals:import-plan` (CREATE:TAX_RULE:ALL). A commit must
 * carry the `planHash` the dry run returned, so it can only create what was
 * reviewed. `additionalEvidenceIds` are approved evidence documents attached
 * to every created proposal (for example the legal text of the burden).
 */
export const proposalPlanImportRequestSchema = z
    .object({
    schemaVersion: z.literal(PROPOSAL_PLAN_SCHEMA_VERSION),
    mode: proposalPlanImportModeSchema,
    plan: proposalPlanSchema,
    expectedPlanHash: planHashSchema.optional(),
    additionalEvidenceIds: z
        .array(uuidSchema)
        .max(PROPOSAL_PLAN_MAX_ADDITIONAL_EVIDENCE)
        .default([]),
    reason: z.string().trim().min(10).max(1_000).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.mode === "COMMIT" && !value.expectedPlanHash) {
        context.addIssue({
            code: "custom",
            path: ["expectedPlanHash"],
            message: "A commit must carry the plan hash returned by the dry run",
        });
    }
    if (new Set(value.additionalEvidenceIds).size !== value.additionalEvidenceIds.length) {
        context.addIssue({
            code: "custom",
            path: ["additionalEvidenceIds"],
            message: "Evidence IDs must be unique",
        });
    }
});
/* -- result ------------------------------------------------------------------ */
/**
 * NEW        would be created (dry run) and was not created yet.
 * CREATED    created by this commit as a PENDING_REVIEW proposal.
 * DUPLICATE  this plan item was already imported; `proposalId` links it.
 * STALE      the active ruleset no longer matches what the plan assumed.
 * INVALID    the item cannot become a proposal (evidence, policy).
 */
export const proposalPlanItemStatusSchema = z.enum([
    "NEW",
    "CREATED",
    "DUPLICATE",
    "STALE",
    "INVALID",
]);
export const proposalPlanItemResultSchema = z
    .object({
    index: z.number().int().nonnegative(),
    supersedesRuleId: uuidSchema,
    route: z.string().max(20),
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    status: proposalPlanItemStatusSchema,
    /** STALE: RULE_NOT_FOUND, RULE_CHANGED, ALREADY_STATED. INVALID: EVIDENCE_REJECTED, POLICY_VIOLATION. */
    reasonCode: codeSchema.nullable(),
    detail: z.string().max(1_000).nullable(),
    /** Paths of the active rule that differ from what the plan assumed (STALE). */
    changedFields: z.array(z.string().max(200)).max(20),
    /** The one field the plan changes, as the reviewer will see it. */
    change: z
        .object({
        field: z.string().max(200),
        from: z.string().max(100).nullable(),
        to: z.string().max(100).nullable(),
    })
        .strict(),
    legalBasis: z
        .object({
        legalReference: z.string().max(500),
        sourceUrl: z.string().max(2_000).nullable(),
        validFrom: z.string().date().nullable(),
        validTo: z.string().date().nullable(),
        note: z.string().max(1_000).nullable(),
    })
        .strict()
        .nullable(),
    /**
     * Where the proposal's evidence comes from. PLAN: the plan's own approved evidence.
     * ACTIVE_RULE: the plan's evidence is not valid in this organization, so the evidence the
     * active rule was published with is inherited ("evidência herdada da regra ativa").
     * ADDITIONAL_ONLY: neither the plan's nor the active rule's evidence is approved here; only
     * the approved additionalEvidenceIds are used. Unapproved evidence is never accepted.
     */
    evidenceSource: z.enum(["PLAN", "ACTIVE_RULE", "ADDITIONAL_ONLY"]),
    /** The created proposal (CREATED) or the one this item was imported as (DUPLICATE). */
    proposalId: uuidSchema.nullable(),
})
    .strict();
export const proposalPlanImportResultSchema = z
    .object({
    schemaVersion: z.literal(PROPOSAL_PLAN_SCHEMA_VERSION),
    mode: proposalPlanImportModeSchema,
    kind: proposalPlanKindSchema,
    /** sha256 of the plan content (kind, criteria, items); stable across re-imports. */
    planHash: planHashSchema,
    /** Policy a draft ruleset needs before the proposals of this plan can be published. */
    requiredPolicyVersion: z.string().min(1).max(50),
    activeRuleset: z
        .object({
        id: uuidSchema,
        version: z.string().min(1).max(100),
        policyVersion: z.string().min(1).max(50),
        contentDigest: z.string().min(1).max(100),
    })
        .strict(),
    /** The open draft ruleset, when there is one, so the next step can link it. */
    draftRuleset: z
        .object({
        id: uuidSchema,
        version: z.string().min(1).max(100),
        policyVersion: z.string().min(1).max(50),
    })
        .strict()
        .nullable(),
    additionalEvidenceIds: z.array(uuidSchema).max(PROPOSAL_PLAN_MAX_ADDITIONAL_EVIDENCE),
    summary: z
        .object({
        total: z.number().int().nonnegative(),
        new: z.number().int().nonnegative(),
        created: z.number().int().nonnegative(),
        duplicate: z.number().int().nonnegative(),
        stale: z.number().int().nonnegative(),
        invalid: z.number().int().nonnegative(),
    })
        .strict(),
    items: z.array(proposalPlanItemResultSchema).max(PROPOSAL_PLAN_MAX_ITEMS),
})
    .strict();
/* -- active ruleset export --------------------------------------------------- */
/**
 * `GET /v1/rulesets/active/export` (READ:TAX_RULE:ALL): the content of the active published
 * ruleset, read only, in the shape the plan scripts read (`fiscalRules` at the top level).
 */
export const activeRulesetExportSchema = z
    .object({
    schemaVersion: z.literal(PROPOSAL_PLAN_SCHEMA_VERSION),
    kind: z.literal("active-ruleset-export"),
    exportedAt: z.string().datetime(),
    ruleset: z
        .object({
        id: uuidSchema,
        version: z.string().min(1).max(100),
        policyVersion: z.string().min(1).max(50),
        contentDigest: z.string().min(1).max(100),
        publishedAt: z.string().min(1).max(40),
    })
        .strict(),
    fiscalRules: z.array(z.record(z.string(), z.unknown())),
    operationalCostRules: z.array(z.record(z.string(), z.unknown())),
    commercialFeeRules: z.array(z.record(z.string(), z.unknown())),
})
    .strict();
//# sourceMappingURL=proposal-plan.js.map